import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Avatar,
  IconButton,
  Box,
  Chip,
  Alert,
  Typography,
  Paper,
  CircularProgress
} from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { EDITABLE_PROFILES } from '../../constants/profiles';
import api, { userService, larService, getPhotoUrl } from '../../services/api';
import { log } from '../../utils/logger';

const PROFILE_OPTIONS = [
  'Administrador',
  'AuxiliarEnfermagem',
  'Cuidador',
  'Enfermeiro',
  'Familiar',
  'Gestor',
  'Medico',
  'Residente',
  'TecnicoEnfermagem',
  'Outro'
];

const GENDER_OPTIONS = [
  { value: 'Masculino', label: 'Masculino' },
  { value: 'Feminino', label: 'Feminino' },
  { value: 'Outro', label: 'Outro' }
];

const PessoaForm = ({ open, onClose, onSubmit, pessoa = null, mode = 'create', setUser }) => {
  const currentUser = JSON.parse(localStorage.getItem('user'));
  
  // Função para extrair código do país e número de telefone
  const extractPhoneInfo = (phoneString) => {
    if (!phoneString) return { countryCode: '55', phoneNumber: '' };
    
    // Verificar se o telefone já tem o formato +XX YYYYY
    const match = phoneString.match(/^\+(\d+)\s(.+)$/);
    if (match) {
      return {
        countryCode: match[1],
        phoneNumber: match[2]
      };
    }
    
    // Caso não tenha o formato esperado, assumir que é apenas o número
    return {
      countryCode: '55', // Código padrão do Brasil
      phoneNumber: phoneString
    };
  };

  const [formData, setFormData] = useState({
    name: '',
    nickname: '',
    email: '',
    password: '',
    profile: '',
    gender: '',
    phone1: '',
    phone2: '',
    countryCode1: '55', // Código do país para telefone principal
    countryCode2: '55', // Código do país para telefone secundário
    birthday: '',
    notes: '',
    active: true,
    photo: null
  });
  const [errors, setErrors] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedLares, setSelectedLares] = useState([]);
  const [availableLares, setAvailableLares] = useState([]); // Inicializado como array vazio
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [loadingLares, setLoadingLares] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Determina se o formulário está em modo somente leitura
  const isReadOnly = mode === 'view';

  // Função para renderizar campos somente leitura
  const renderReadOnlyField = (label, value) => (
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label={label}
        value={value || ''}
        margin="normal"
        InputProps={{
          readOnly: true,
        }}
        variant="filled"
      />
    </Grid>
  );

  useEffect(() => {
    // Limpar o estado quando o formulário é fechado
    if (!open) {
      setFormData({
        name: '',
        nickname: '',
        email: '',
        password: '',
        profile: '',
        gender: '',
        phone1: '',
        phone2: '',
        countryCode1: '55',
        countryCode2: '55',
        birthday: '',
        notes: '',
        active: true,
        photo: null
      });
      setPhotoPreview(null);
      setSelectedLares([]);
      setErrors({});
    }
  }, [open]);

  useEffect(() => {
    if (pessoa && (mode === 'edit' || mode === 'view')) {
      log('Carregando dados da pessoa para edição:', pessoa, 'info', true);
      
      // Extrair informações de telefone
      const { countryCode: countryCode1, phoneNumber: phone1 } = extractPhoneInfo(pessoa.phone1);
      const { countryCode: countryCode2, phoneNumber: phone2 } = extractPhoneInfo(pessoa.phone2);
      
      // Preencher formulário com dados da pessoa
      setFormData({
        name: pessoa.name || '',
        nickname: pessoa.nickname || '',
        email: pessoa.email || '',
        password: '', // Não preencher senha
        profile: pessoa.profile || '',
        gender: pessoa.gender || '',
        phone1: phone1 || '',
        phone2: phone2 || '',
        countryCode1: countryCode1 || '55',
        countryCode2: countryCode2 || '55',
        birthday: pessoa.birthday || '',
        notes: pessoa.notes || '',
        active: pessoa.active !== undefined ? pessoa.active : true,
        photo: null // A foto será carregada separadamente
      });
      
      // Carregar preview da foto se existir
      if (pessoa.photo) {
        try {
          const photoUrl = getPhotoUrl(pessoa.photo);
          log('URL da foto carregada:', photoUrl, 'info', true);
          setPhotoPreview(photoUrl);
        } catch (error) {
          log('Erro ao carregar preview da foto:', error, 'error', true);
        }
      }
      
      // Carregar lares vinculados
      if (pessoa.laresVinculados && Array.isArray(pessoa.laresVinculados)) {
        const laresIds = pessoa.laresVinculados.map(lar => lar.id);
        log('Lares vinculados carregados:', laresIds, 'info', true);
        setSelectedLares(laresIds);
      }
    }
  }, [pessoa, mode]);

  useEffect(() => {
    fetchLares();
  }, []);

  const fetchLares = async () => {
    setLoadingLares(true);
    try {
      log('Buscando lares disponíveis...', null, 'info', true);
      const response = await larService.getAll();
      log('Resposta da API de lares:', response, 'info', true);
      
      if (response && response.data) {
        let laresData;
        
        // Verificar se a resposta tem o formato esperado (array direto ou dentro de um objeto)
        if (Array.isArray(response.data)) {
          laresData = response.data;
          log('Lares disponíveis (formato array):', laresData, 'info', true);
        } else if (response.data.lares && Array.isArray(response.data.lares)) {
          laresData = response.data.lares;
          log('Lares disponíveis (formato objeto com propriedade lares):', laresData, 'info', true);
        } else if (typeof response.data === 'object' && Object.values(response.data).some(item => item && typeof item === 'object' && 'nome' in item)) {
          // Caso seja um objeto com lares como valores
          laresData = Object.values(response.data).filter(item => item && typeof item === 'object' && 'nome' in item);
          log('Lares disponíveis (formato objeto de objetos):', laresData, 'info', true);
        } else {
          log('Formato inesperado na resposta da API de lares:', response.data, 'warn', true);
          laresData = [];
        }
        
        // Verificar se os lares têm o campo nome
        laresData.forEach((lar, index) => {
          if (!lar.nome) {
            log(`Aviso: Lar na posição ${index} não possui o campo 'nome':`, lar, 'warn', true);
          }
        });
        
        // Ordenar lares por nome
        laresData.sort((a, b) => {
          const nomeA = a.nome || '';
          const nomeB = b.nome || '';
          return nomeA.localeCompare(nomeB);
        });
        
        setAvailableLares(laresData);
        
        // Se já temos selectedLares, vamos verificar se todos existem na lista de lares disponíveis
        if (selectedLares.length > 0) {
          const laresNaoEncontrados = selectedLares.filter(
            larId => !laresData.some(lar => lar.id === larId)
          );
          
          if (laresNaoEncontrados.length > 0) {
            log('Aviso: Alguns lares selecionados não foram encontrados na lista de lares disponíveis:', laresNaoEncontrados, 'warn', true);
          }
        }
      } else {
        log('Aviso: A resposta da API de lares não contém dados:', response, 'warn', true);
        setAvailableLares([]);
      }
    } catch (error) {
      log('Erro ao carregar lares:', error, 'error', true);
      setAvailableLares([]);
    } finally {
      setLoadingLares(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpar erro do campo quando ele for alterado
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Função para lidar com o upload de foto
  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    log('Arquivo de foto selecionado:', {
      name: file.name,
      type: file.type,
      size: file.size
    }, 'info', true);
    
    // Validar tamanho (2MB)
    const MAX_SIZE = 2 * 1024 * 1024; // 2MB em bytes
    if (file.size > MAX_SIZE) {
      setErrors(prev => ({
        ...prev,
        photo: 'A imagem deve ter no máximo 2MB'
      }));
      return;
    }
    
    // Validar tipo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({
        ...prev,
        photo: 'Formato de imagem inválido. Use JPEG, PNG ou WebP'
      }));
      return;
    }
    
    try {
      // Criar preview da imagem
      const objectUrl = URL.createObjectURL(file);
      setPhotoPreview(objectUrl);
      
      // Atualizar formData com a nova foto
      setFormData(prev => ({
        ...prev,
        photo: file
      }));
      
      // Limpar erro se existir
      setErrors(prev => ({
        ...prev,
        photo: null
      }));
      
      // Se estiver editando um usuário existente, fazer upload imediato da foto
      if (mode === 'edit' && pessoa && pessoa.id) {
        try {
          setUploadingPhoto(true);
          
          // Criar FormData apenas para a foto
          const photoFormData = new FormData();
          photoFormData.append('photo', file);
          
          // Fazer upload da foto
          const response = await userService.updatePhoto(pessoa.id, photoFormData);
          log('Foto atualizada com sucesso:', response.data, 'info', true);
          
          // Atualizar preview com a URL retornada pelo servidor (com timestamp para evitar cache)
          if (response.data && response.data.photo) {
            // Usar a URL retornada pelo servidor que já contém timestamp
            const photoUrl = response.data.photo;
            
            // Atualizar o preview com a URL completa
            setPhotoPreview(getPhotoUrl(photoUrl));
            
            // Atualizar o formData com a nova foto URL
            setFormData(prev => ({
              ...prev,
              photo: photoUrl
            }));
            
            // Se o usuário logado é o mesmo que está sendo editado, atualizar o contexto de autenticação
            if (currentUser && currentUser.id === pessoa.id) {
              try {
                // Criar um objeto de usuário atualizado com a nova foto
                const updatedUser = {
                  ...currentUser,
                  photo: photoUrl
                };
                
                // Atualizar o usuário no localStorage
                localStorage.setItem('user', JSON.stringify(updatedUser));
                
                // Atualizar o contexto de autenticação
                if (setUser) {
                  setUser(updatedUser);
                }
                
                log('Contexto de autenticação atualizado com a nova foto', { userId: currentUser.id }, 'info', true);
              } catch (userError) {
                log('Erro ao atualizar contexto de autenticação:', userError, 'error', true);
              }
            }
          }
          
          // Mostrar mensagem de sucesso temporária
          setSubmitError({
            severity: 'success',
            message: 'Foto atualizada com sucesso!'
          });
          
          setTimeout(() => {
            setSubmitError(null);
          }, 3000);
        } catch (uploadError) {
          log('Erro ao fazer upload da foto:', uploadError, 'error', true);
          setSubmitError({
            severity: 'error',
            message: 'Erro ao atualizar a foto. Tente novamente.'
          });
        } finally {
          setUploadingPhoto(false);
        }
      }
    } catch (error) {
      log('Erro ao processar imagem:', error, 'error', true);
      setErrors(prev => ({
        ...prev,
        photo: 'Erro ao processar a imagem'
      }));
    }
  };

  const handlePhoneChange = (value, data, field) => {
    const phoneNumber = value.replace(/\D/g, ''); // Remove todos os não-dígitos
    const countryCode = data.dialCode;
    const nationalNumber = phoneNumber.slice(data.dialCode.length);
    
    log(`Phone Change - Field: ${field}, Value: ${value}, Country Code: ${countryCode}, National Number: ${nationalNumber}`, null, 'info', true);
    
    setFormData(prev => ({
      ...prev,
      [field]: nationalNumber,
      [`countryCode${field === 'phone1' ? '1' : '2'}`]: countryCode
    }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    // Verificar se e é um evento e tem preventDefault
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    
    setLoading(true);
    setSubmitError(null);
    
    try {
      // Validação básica
      const newErrors = {};
      if (!formData.name?.trim()) newErrors.name = 'Nome é obrigatório';
      if (!formData.email?.trim()) newErrors.email = 'Email é obrigatório';
      if (mode === 'create' && !formData.password?.trim()) newErrors.password = 'Senha é obrigatória';
      if (!formData.profile) newErrors.profile = 'Perfil é obrigatório';
      if (!formData.gender) newErrors.gender = 'Gênero é obrigatório';
      if (!formData.birthday) newErrors.birthday = 'Data de nascimento é obrigatória';
      
      // Se houver erros, não prossegue
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setLoading(false);
        return;
      }

      // Criar FormData para envio
      const submitData = new FormData();
      
      // Concatenar código do país com os números de telefone
      const dataToSubmit = { ...formData };
      if (dataToSubmit.phone1) {
        dataToSubmit.phone1 = `+${dataToSubmit.countryCode1} ${dataToSubmit.phone1}`;
      }
      if (dataToSubmit.phone2) {
        dataToSubmit.phone2 = `+${dataToSubmit.countryCode2} ${dataToSubmit.phone2}`;
      }
      
      // Adicionar campos ao FormData, excluindo valores vazios
      Object.keys(dataToSubmit).forEach(key => {
        // Não enviar os códigos de país separadamente, pois já foram concatenados
        if (key !== 'countryCode1' && key !== 'countryCode2') {
          if (dataToSubmit[key] !== null && dataToSubmit[key] !== undefined && dataToSubmit[key] !== '') {
            if (key === 'photo' && dataToSubmit[key] instanceof File) {
              submitData.append('photo', dataToSubmit[key]);
            } else if (key !== 'photo') {
              submitData.append(key, dataToSubmit[key].toString());
            }
          }
        }
      });

      // Adicionar lares selecionados
      if (selectedLares && selectedLares.length > 0) {
        log('Adicionando lares vinculados ao formulário:', selectedLares, 'info', true);
        
        // Verificar se todos os lares selecionados existem na lista de lares disponíveis
        const laresInvalidos = selectedLares.filter(id => 
          !availableLares.some(lar => lar.id === id)
        );
        
        if (laresInvalidos.length > 0) {
          log('Aviso: Alguns lares selecionados não foram encontrados na lista de lares disponíveis:', laresInvalidos, 'warn', true);
        }
        
        // Adicionar ao FormData como JSON
        submitData.append('laresVinculados', JSON.stringify(selectedLares));
        
        // Adicionar também como array de lares
        submitData.append('lares', JSON.stringify(selectedLares));
        
        // Adicionar também como campo individual para garantir compatibilidade
        selectedLares.forEach((larId, index) => {
          submitData.append(`lares[${index}]`, larId);
        });
      } else if (mode === 'edit' && (!selectedLares || selectedLares.length === 0)) {
        // Se estamos no modo de edição e não há lares selecionados
        if (pessoa && pessoa.laresVinculados && pessoa.laresVinculados.length > 0) {
          // Se a pessoa já tinha lares vinculados e não alteramos explicitamente para removê-los,
          // enviar os lares originais para manter as associações
          log('Mantendo lares originais da pessoa:', pessoa.laresVinculados.map(lar => lar.id), 'info', true);
          const laresIds = pessoa.laresVinculados.map(lar => lar.id);
          submitData.append('laresVinculados', JSON.stringify(laresIds));
          laresIds.forEach((larId, index) => {
            submitData.append(`lares[${index}]`, larId);
          });
        } else {
          // Se a pessoa não tinha lares vinculados ou estamos explicitamente removendo todos,
          // enviar array vazio
          log('Enviando array vazio de lares explicitamente', null, 'info', true);
          submitData.append('laresVinculados', JSON.stringify([]));
          submitData.append('lares', JSON.stringify([]));
        }
      } else if (mode === 'create') {
        // Se estamos criando um novo usuário sem lares, enviar array vazio
        log('Criando novo usuário sem lares vinculados', null, 'info', true);
        submitData.append('laresVinculados', JSON.stringify([]));
        submitData.append('lares', JSON.stringify([]));
      }

      // Log para depuração
      log('Dados a serem enviados:', null, 'info', true);
      for (let [key, value] of submitData.entries()) {
        if (key === 'photo') {
          log('photo:', 'Arquivo de imagem', 'info', true);
        } else {
          log(key + ':', value, 'info', true);
        }
      }

      log('Enviando dados da pessoa com FormData', null, 'info', true);
      const response = await onSubmit(submitData);
      
      if (response?.error) {
        throw new Error(response.error);
      }
      
      onClose();
    } catch (error) {
      log('Erro ao enviar formulário:', error, 'error', true);
      setSubmitError({
        severity: 'error',
        message: error.message || 'Erro ao salvar os dados. Tente novamente.'
      });
    } finally {
      setLoading(false);
    }
  };

  const canEditProfile = currentUser?.profile === 'Administrador' || 
                        (currentUser?.profile === 'Gestor' && 
                         (!pessoa || EDITABLE_PROFILES.includes(pessoa.profile)));

  useEffect(() => {
    return () => {
      // Verificar se o photoPreview é uma URL de objeto
      if (photoPreview && photoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  return (
    <Dialog 
      open={open} 
      onClose={() => !loading && onClose()}
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        {mode === 'create' ? 'Criar Novo Usuário' : mode === 'view' ? 'Visualizar Usuário' : 'Editar Usuário'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {submitError && (
            <Alert severity={submitError.severity || "error"} sx={{ mb: 2 }}>
              {submitError.message}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} container justifyContent="center" alignItems="center">
              <Box position="relative" display="inline-block">
                <Avatar
                  src={photoPreview}
                  sx={{ width: 100, height: 100, mb: 2 }}
                />
                {!isReadOnly && (
                  <IconButton
                    color="primary"
                    aria-label="upload picture"
                    component="label"
                    sx={{
                      position: 'absolute',
                      bottom: 20,
                      right: -20,
                    }}
                    disabled={uploadingPhoto}
                  >
                    <input
                      hidden
                      accept="image/jpeg,image/png,image/webp"
                      type="file"
                      onChange={handlePhotoChange}
                    />
                    {uploadingPhoto ? <CircularProgress size={24} /> : <PhotoCamera />}
                  </IconButton>
                )}
              </Box>
              {errors.photo && (
                <Typography color="error" variant="caption" display="block" textAlign="center" width="100%">
                  {errors.photo}
                </Typography>
              )}
            </Grid>

            {isReadOnly ? (
              // Campos somente leitura
              <>
                {renderReadOnlyField('Nome', formData.name)}
                {renderReadOnlyField('Apelido', formData.nickname)}
                {renderReadOnlyField('E-mail', formData.email)}
                {renderReadOnlyField('Perfil', formData.profile)}
                {renderReadOnlyField('Gênero', formData.gender)}
                {renderReadOnlyField('Telefone Principal', formData.phone1)}
                {renderReadOnlyField('Telefone Secundário', formData.phone2)}
                {renderReadOnlyField('Data de Nascimento', formData.birthday)}
                {renderReadOnlyField('Status', formData.active ? 'Ativo' : 'Inativo')}
                {renderReadOnlyField('Lares Vinculados', 
                  loadingLares 
                  ? 'Carregando lares...' 
                  : (Array.isArray(selectedLares) && selectedLares.length > 0
                    ? (Array.isArray(availableLares) && availableLares.length > 0
                      ? selectedLares.map(larId => {
                          const lar = availableLares.find(l => l.id === larId);
                          return lar ? lar.nome : `Lar ID: ${larId}`;
                        }).join(', ')
                      : 'Carregando detalhes dos lares...')
                    : 'Nenhum lar vinculado'))}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Observações"
                    value={formData.notes || ''}
                    margin="normal"
                    InputProps={{
                      readOnly: true,
                    }}
                    variant="filled"
                    multiline
                    rows={4}
                  />
                </Grid>
              </>
            ) : (
              // Campos editáveis
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Nome"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    disabled={isReadOnly}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Apelido"
                    name="nickname"
                    value={formData.nickname}
                    onChange={handleChange}
                    disabled={isReadOnly}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    disabled={isReadOnly}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={mode === 'create' ? 'Senha (Obrigatória)' : 'Senha (Deixe em branco para manter a atual)'}
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    error={!!errors.password}
                    helperText={errors.password}
                    disabled={isReadOnly}
                    required={mode === 'create'}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.profile} required>
                    <InputLabel>Perfil</InputLabel>
                    <Select
                      name="profile"
                      value={formData.profile || ''}
                      onChange={handleChange}
                      disabled={!canEditProfile}
                    >
                      {PROFILE_OPTIONS.map(option => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                    {errors.profile && <FormHelperText>{errors.profile}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.gender} required>
                    <InputLabel>Gênero</InputLabel>
                    <Select
                      name="gender"
                      value={formData.gender || ''}
                      onChange={handleChange}
                    >
                      {GENDER_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.phone1}>
                    <PhoneInput
                      country="br"
                      value={formData.countryCode1 + formData.phone1}
                      onChange={(value, data) => handlePhoneChange(value, data, 'phone1')}
                      inputProps={{
                        required: true,
                        name: 'phone1'
                      }}
                      specialLabel="Telefone Principal"
                      countryCodeEditable={true}
                      enableSearch={true}
                      searchPlaceholder="Buscar país..."
                    />
                    {errors.phone1 && <FormHelperText>{errors.phone1}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.phone2}>
                    <PhoneInput
                      country="br"
                      value={formData.countryCode2 + (formData.phone2 || '')}
                      onChange={(value, data) => handlePhoneChange(value, data, 'phone2')}
                      inputProps={{
                        name: 'phone2'
                      }}
                      specialLabel="Telefone Secundário"
                      countryCodeEditable={true}
                      enableSearch={true}
                      searchPlaceholder="Buscar país..."
                    />
                    {errors.phone2 && <FormHelperText>{errors.phone2}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Data de Nascimento"
                    type="date"
                    name="birthday"
                    value={formData.birthday || ''}
                    onChange={handleChange}
                    error={!!errors.birthday}
                    helperText={errors.birthday}
                    required
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={isReadOnly}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="active"
                      value={formData.active}
                      onChange={handleChange}
                      disabled={!canEditProfile}
                    >
                      <MenuItem value={true}>Ativo</MenuItem>
                      <MenuItem value={false}>Inativo</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="lares-vinculados-label">Lares Vinculados</InputLabel>
                    <Select
                      labelId="lares-vinculados-label"
                      multiple
                      value={selectedLares}
                      onChange={(e) => setSelectedLares(e.target.value)}
                      disabled={isReadOnly}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            // Garantir que availableLares é um array antes de usar find
                            const lar = Array.isArray(availableLares) 
                              ? availableLares.find(lar => lar.id === value)
                              : null;
                            return (
                              <Chip 
                                key={value} 
                                label={lar ? lar.nome : `Lar ID: ${value}`}
                                onDelete={isReadOnly ? undefined : () => {
                                  setSelectedLares(selectedLares.filter(id => id !== value));
                                }}
                                deleteIcon={isReadOnly ? undefined : undefined}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {loadingLares ? (
                        <MenuItem disabled>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CircularProgress size={24} sx={{ mr: 1 }} />
                            <Typography>Carregando lares...</Typography>
                          </Box>
                        </MenuItem>
                      ) : Array.isArray(availableLares) && availableLares.length > 0 ? (
                        availableLares.map((lar) => (
                          <MenuItem key={lar.id} value={lar.id}>
                            {lar.nome || `Lar ID: ${lar.id}`}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>
                          <Typography color="error">Nenhum lar disponível</Typography>
                        </MenuItem>
                      )}
                    </Select>
                    <FormHelperText>
                      {isReadOnly 
                        ? 'Lares vinculados a esta pessoa' 
                        : 'Selecione os lares vinculados a esta pessoa'}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Anotações Importantes"
                    name="notes"
                    value={formData.notes || ''}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    inputProps={{ maxLength: 255 }}
                    helperText={`${(formData.notes || '').length}/255 caracteres`}
                    disabled={isReadOnly}
                  />
                </Grid>

                {errors.submit && (
                  <Grid item xs={12}>
                    <Alert severity="error">{errors.submit}</Alert>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => !loading && onClose()} 
            disabled={loading}
          >
            Cancelar
          </Button>
          {!isReadOnly && (
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Salvar'}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PessoaForm;
